import React, { useCallback, useEffect, useState } from 'react'
import { useParticipantsList } from './hooks'
import {
  Button,
  Flex,
  Input,
  message,
  Select,
  Table,
  Tag,
  Typography,
} from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { Participant } from './types'
import { path } from 'src/libs'
import { getNumberFormat } from 'src/libs/utils'
import {
  ExperimentOutlined,
  CrownOutlined,
  ReloadOutlined,
  StopOutlined,
  UnlockOutlined,
} from '@ant-design/icons'
import { updateParticipantFx } from './model'
import { BanModal } from '../widgets/BanModal/component'
import { UnbanModal } from '../widgets/UnbanModal/component'
import { useUrlFilters } from 'src/libs/useUrlFilters'
import styles from './styles.module.css'
import { Statuses } from './constants'

const { Text } = Typography

const STATUS_COLOR: Record<string, string> = {
  active: 'green',
  ending: 'default',
  ended: 'default',
  closed: 'default',
  auto_banned: 'volcano',
  banned: 'red',
}

const STATUS_BACKEND_MAP: Record<string, string[]> = {
  active: ['active'],
  close: ['ending', 'ended', 'closed'],
  'auto-disqualified': ['auto_banned'],
  disqualified: ['banned'],
}

const WITHDRAWAL_COLOR: Record<string, string> = {
  instant: 'blue',
  trading: 'purple',
}

// ── Filter config ──────────────────────────────────────────
const FILTER_CONFIG = {
  clientId: { type: 'string' as const },
  email: { type: 'string' as const },
  tsname: { type: 'string' as const },
  status: { type: 'array' as const },
  withdrawalType: { type: 'array' as const },
  page: { type: 'string' as const, default: '1', pagination: true },
  pageSize: { type: 'string' as const, default: '15', pagination: true },
  sortField: {
    type: 'string' as const,
    default: 'registeredTs',
    pagination: true,
  },
  sortOrder: { type: 'string' as const, default: 'descend', pagination: true },
}

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Close', value: 'close' },
  { label: 'Disqualified', value: 'disqualified' },
  { label: 'Auto-disqualified', value: 'auto-disqualified' },
]

const WITHDRAWAL_TYPE_OPTIONS = [
  { label: 'Instant', value: 'without_trade' },
  { label: 'Trading', value: 'with_trade' },
]

export const ParticipantsList: React.FC = () => {
  const {
    participants,
    loading,
    totalCountOfParticipants,
    onChangeParticipantsList,
    banParticipant,
    unbanParticipant,
    updateLoading,
  } = useParticipantsList()

  const { filters, setFilters, resetFilters } = useUrlFilters(FILTER_CONFIG)

  const page = Number(filters.page) || 1
  const pageSize = Number(filters.pageSize) || 15
  const orderBy = filters.sortField || 'registeredTs'
  const orderDirection = filters.sortOrder === 'ascend' ? '' : '-'

  // Local input state — applied to URL only on Enter/Search
  const [inputClientId, setInputClientId] = useState(filters.clientId || '')
  const [inputEmail, setInputEmail] = useState(filters.email || '')
  const [inputName, setInputName] = useState(filters.tsname || '')

  const [banModal, setBanModal] = useState<{
    open: boolean
    participant: Participant | null
  }>({ open: false, participant: null })

  const [unbanModal, setUnbanModal] = useState<{
    open: boolean
    participant: Participant | null
  }>({ open: false, participant: null })

  const [refreshTrigger, setRefreshTrigger] = useState(0)

  useEffect(() => {
    const unsub = updateParticipantFx.doneData.watch(() => {
      setBanModal({ open: false, participant: null })
      setUnbanModal({ open: false, participant: null })
      setRefreshTrigger((prev) => prev + 1)
    })
    const unsubFail = updateParticipantFx.failData.watch(() => {
      message.error('Ошибка. Попробуйте снова.')
    })
    return () => {
      unsub()
      unsubFail()
    }
  }, [])

  // ── Build API filters from URL state ───────────────────
  const buildApiFilters = useCallback(() => {
    const apiFilters: Record<string, any> = {}
    if (filters.clientId) apiFilters.clientId = filters.clientId
    if (filters.email) apiFilters.email = filters.email
    if (filters.tsname) apiFilters.tsname = filters.tsname
    if (filters.status && filters.status.length > 0)
      apiFilters.status = filters.status.flatMap(
        (filterValue) => STATUS_BACKEND_MAP[filterValue] ?? [filterValue],
      )
    if (filters.withdrawalType && filters.withdrawalType.length > 0)
      apiFilters.withdrawalType = filters.withdrawalType
    return Object.keys(apiFilters).length > 0 ? apiFilters : undefined
  }, [filters])

  // ── Fetch data when filters change ──────────────────────
  useEffect(() => {
    onChangeParticipantsList({
      limit: pageSize,
      page,
      orderBy,
      orderDirection,
      filters: buildApiFilters(),
    })
  }, [filters, page, pageSize, orderBy, orderDirection, refreshTrigger])

  // ── Apply text search ──────────────────────────────────
  const applySearch = (key: 'clientId' | 'email' | 'tsname', value: string) => {
    const trimmedValue = value.trim()
    if (trimmedValue.length > 0 && trimmedValue.length < 3) return
    setFilters({ [key]: trimmedValue || undefined, page: undefined } as any)
  }

  const clearSearch = (
    key: 'clientId' | 'email' | 'tsname',
    setter: (value: string) => void,
  ) => {
    setter('')
    setFilters({ [key]: undefined, page: undefined } as any)
  }

  // ── Active filter tags ─────────────────────────────────
  const activeTags = React.useMemo(() => {
    const tags: { key: string; label: string; clear: () => void }[] = []
    if (filters.clientId)
      tags.push({
        key: 'clientId',
        label: `ID: ${filters.clientId}`,
        clear: () => clearSearch('clientId', setInputClientId),
      })
    if (filters.email)
      tags.push({
        key: 'email',
        label: `Email: ${filters.email}`,
        clear: () => clearSearch('email', setInputEmail),
      })
    if (filters.tsname)
      tags.push({
        key: 'tsname',
        label: `Name: ${filters.tsname}`,
        clear: () => clearSearch('tsname', setInputName),
      })
    filters.status?.forEach((statusValue) =>
      tags.push({
        key: `status-${statusValue}`,
        label: `Status: ${statusValue}`,
        clear: () =>
          setFilters({
            status: filters.status?.filter((item) => item !== statusValue),
            page: undefined,
          } as any),
      }),
    )
    filters.withdrawalType?.forEach((withdrawalTypeValue) =>
      tags.push({
        key: `wt-${withdrawalTypeValue}`,
        label: `Type: ${withdrawalTypeValue}`,
        clear: () =>
          setFilters({
            withdrawalType: filters.withdrawalType?.filter(
              (item) => item !== withdrawalTypeValue,
            ),
            page: undefined,
          } as any),
      }),
    )
    return tags
  }, [filters])

  const columns: ColumnsType<Participant> = [
    {
      title: 'User ID',
      dataIndex: ['client', 'clientId'],
      width: 130,
      render: (_value: any, record: Participant) => (
        <Flex align="center" gap={4} wrap="wrap" justify="center">
          <a
            href={path.clients.details(String(record.client.clientId))}
            target="_blank"
            style={{ fontSize: 14, fontWeight: 600 }}
          >
            {record.client.clientId}
          </a>
          {record.client.isTest && (
            <Tag
              icon={<ExperimentOutlined />}
              color="purple"
              style={{ fontSize: 10, margin: 0, padding: '0 4px' }}
            >
              Test
            </Tag>
          )}
          {record.client.isPremium && (
            <Tag
              icon={<CrownOutlined />}
              color="gold"
              style={{ fontSize: 10, margin: 0, padding: '0 4px' }}
            >
              Premium
            </Tag>
          )}
        </Flex>
      ),
    },
    {
      title: 'Name',
      dataIndex: ['client', 'fullName'],
      width: 180,
      render: (fullName: string) => (
        <Text strong style={{ fontSize: 14 }}>
          {fullName || '\u2014'}
        </Text>
      ),
    },
    {
      title: 'Email',
      dataIndex: ['client', 'email'],
      width: 210,
      ellipsis: true,
      render: (email: string) => (
        <Text style={{ fontSize: 14 }}>{email || '\u2014'}</Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 140,
      render: (status: string) => (
        <Tag
          color={STATUS_COLOR[status] || 'default'}
          style={{ fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: 'Withdrawal type',
      dataIndex: 'withdrawalType',
      width: 140,
      render: (withdrawalType: string) =>
        withdrawalType ? (
          <Tag
            color={WITHDRAWAL_COLOR[withdrawalType] || 'default'}
            style={{ fontSize: 11, textTransform: 'capitalize' }}
          >
            {withdrawalType}
          </Tag>
        ) : (
          <Text type="secondary" style={{ fontSize: 13 }}>
            not selected
          </Text>
        ),
    },
    {
      title: 'Bonus Lots',
      dataIndex: 'sumBonusLots',
      sorter: true,
      width: 120,
      align: 'right',
      render: (bonusLots: string) => (
        <Text style={{ fontSize: 14 }}>
          {bonusLots ? getNumberFormat(bonusLots, 2) : '\u2014'}
        </Text>
      ),
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      sorter: true,
      width: 120,
      align: 'right',
      render: (balance: number) => (
        <Text style={{ fontSize: 14 }}>
          {balance != null ? getNumberFormat(balance, 2) : '\u2014'}
        </Text>
      ),
    },
    {
      title: 'Profit',
      dataIndex: 'sumBonusProfit',
      sorter: true,
      width: 120,
      align: 'right',
      render: (profit: string) => (
        <Text style={{ fontSize: 14 }}>
          {profit ? getNumberFormat(profit, 2) : '\u2014'}
        </Text>
      ),
    },
    {
      title: 'Coefficient',
      dataIndex: 'coefficient',
      sorter: true,
      width: 120,
      align: 'right',
      render: (coefficient: string) => (
        <Text style={{ fontSize: 14 }}>
          {coefficient ? getNumberFormat(coefficient, 2) : '\u2014'}
        </Text>
      ),
    },
    {
      title: 'Real lots',
      dataIndex: 'sumCurrentWithdrawalLots',
      sorter: true,
      width: 120,
      align: 'right',
      render: (realLots: string) => (
        <Text style={{ fontSize: 14 }}>
          {realLots ? getNumberFormat(realLots, 2) : '\u2014'}
        </Text>
      ),
    },
    {
      title: 'Withdrawal',
      dataIndex: 'withdrawalAmount',
      sorter: true,
      width: 120,
      align: 'right',
      render: (withdrawalAmount: string) => (
        <Text style={{ fontSize: 14 }}>
          {withdrawalAmount ? getNumberFormat(withdrawalAmount, 2) : '\u2014'}
        </Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      fixed: 'right' as const,
      onHeaderCell: () => ({ style: { background: 'var(--crm-bg, #fff)' } }),
      render: (_: any, record: Participant) => (
        <Flex gap={4} justify="center">
          {record.status !== Statuses.AUTO_BANNED &&
            record.status !== Statuses.BANNED && (
              <Button
                size="small"
                danger
                icon={<StopOutlined />}
                title="Заблокировать"
                onClick={() => setBanModal({ open: true, participant: record })}
              />
            )}
          {record.status === Statuses.AUTO_BANNED && (
            <Button
              size="small"
              icon={<UnlockOutlined />}
              title="Разблокировать"
              onClick={() => setUnbanModal({ open: true, participant: record })}
            />
          )}
        </Flex>
      ),
    },
  ]

  return (
    <div className={styles.tableWrapper}>
      <div
        style={{
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 12,
          border: '1px solid var(--crm-border, #d9d9d9)',
          background: 'var(--crm-bg, #fff)',
        }}
      >
        <Flex gap={6} wrap="wrap">
          <Input.Search
            placeholder="Client ID"
            value={inputClientId}
            onChange={(e) =>
              setInputClientId(e.target.value.replace(/\D/g, ''))
            }
            onSearch={(item) => applySearch('clientId', item)}
            onBlur={(e) => applySearch('clientId', e.target.value)}
            allowClear
            onClear={() => clearSearch('clientId', setInputClientId)}
            style={{ width: 150, fontSize: 14 }}
          />
          <Input.Search
            placeholder="Email"
            value={inputEmail}
            onChange={(e) => setInputEmail(e.target.value)}
            onSearch={(item) => applySearch('email', item)}
            onBlur={(e) => applySearch('email', e.target.value)}
            allowClear
            onClear={() => clearSearch('email', setInputEmail)}
            style={{ width: 250, fontSize: 14 }}
          />
          <Input.Search
            placeholder="Full name"
            value={inputName}
            onChange={(e) => setInputName(e.target.value)}
            onSearch={(item) => applySearch('tsname', item)}
            onBlur={(e) => applySearch('tsname', e.target.value)}
            allowClear
            onClear={() => clearSearch('tsname', setInputName)}
            style={{ width: 220, fontSize: 14 }}
          />
          <Select
            mode="multiple"
            placeholder="Status"
            value={filters.status || []}
            onChange={(value) =>
              setFilters({
                status: value.length > 0 ? value : undefined,
                page: undefined,
              } as any)
            }
            options={STATUS_OPTIONS}
            style={{ minWidth: 160 }}
            maxTagCount="responsive"
            allowClear
          />
          <Select
            mode="multiple"
            placeholder="Withdrawal type"
            value={filters.withdrawalType || []}
            onChange={(value) =>
              setFilters({
                withdrawalType: value.length > 0 ? value : undefined,
                page: undefined,
              } as any)
            }
            options={WITHDRAWAL_TYPE_OPTIONS}
            style={{ minWidth: 170 }}
            maxTagCount="responsive"
            allowClear
          />
        </Flex>

        {activeTags.length > 0 && (
          <>
            <div
              style={{
                height: 1,
                background: 'var(--crm-border, #e2e5ec)',
                margin: '10px 0 8px',
              }}
            />
            <Flex gap={6} wrap="wrap" align="center">
              <Text
                type="secondary"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  whiteSpace: 'nowrap',
                }}
              >
                Active:
              </Text>
              {activeTags.map((tag) => (
                <Tag
                  key={tag.key}
                  closable
                  onClose={tag.clear}
                  style={{ fontSize: 11, margin: 0 }}
                  color="blue"
                >
                  {tag.label}
                </Tag>
              ))}
              <Button
                size="small"
                type="text"
                icon={<ReloadOutlined />}
                onClick={() => {
                  resetFilters()
                  setInputClientId('')
                  setInputEmail('')
                  setInputName('')
                }}
                style={{ color: '#ef4444', fontSize: 11 }}
              >
                Reset all
              </Button>
            </Flex>
          </>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={participants}
        size="small"
        scroll={{ x: 1600 }}
        loading={loading}
        rowKey="bonusAccountId"
        rowClassName={(_record, index) =>
          index % 2 === 1 ? 'ant-table-row-striped' : ''
        }
        pagination={{
          current: page,
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: [15, 30, 50, 100],
          total: totalCountOfParticipants,
          showTotal: (total, [from, to]) => `${from}\u2013${to} of ${total}`,
          size: 'default',
          position: ['topRight', 'bottomRight'],
        }}
        onChange={(pagination, _filters, sorter) => {
          const updates: Record<string, any> = {}
          if (pagination.current)
            updates.page =
              pagination.current === 1 ? undefined : String(pagination.current)
          if (pagination.pageSize && pagination.pageSize !== pageSize) {
            updates.pageSize = String(pagination.pageSize)
            updates.page = undefined
          }
          const result = Array.isArray(sorter) ? sorter[0] : sorter
          if (result?.field || result?.columnKey) {
            const field = result.field || result.columnKey
            updates.sortField = field
            updates.sortOrder =
              result.order === 'descend' ? undefined : 'ascend'
          }
          setFilters(updates as any)
        }}
      />

      <BanModal
        open={banModal.open}
        participant={banModal.participant}
        loading={updateLoading}
        onClose={() => setBanModal({ open: false, participant: null })}
        onSubmit={(banReason) => {
          if (banModal.participant)
            banParticipant({
              id: banModal.participant.client.clientId,
              banReason,
            })
        }}
      />

      <UnbanModal
        open={unbanModal.open}
        participant={unbanModal.participant}
        loading={updateLoading}
        onClose={() => setUnbanModal({ open: false, participant: null })}
        onSubmit={(banReason) => {
          if (unbanModal.participant)
            unbanParticipant({
              id: unbanModal.participant.client.clientId,
              banReason,
            })
        }}
      />
    </div>
  )
}
